import { initialProps } from '@utils/initial'
import { Request } from '@group-devs/yfp7-global-modules'
import Error from '../404'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';

import InfiniteScroll from 'react-infinite-scroll-component';
import { Row, Col, ProgressBar, Button, DropdownButton, Dropdown } from 'react-bootstrap'
import { FaCheck, FaSortAmountDown, FaChevronDown, FaBars } from 'react-icons/fa'
import Link from 'next/link'
import { WellnessLayout } from '@components/Block'
import herostyles from '@group-devs/yfp7-global-modules/components/hero.module.css'
import { HeroWrapper } from '@group-devs/yfp7-global-modules/components/Block'

import params from '@data/params'
import sectionData from '@data/sections'
import categories from '@data/wellnessCategories'
import Image from 'next/image'

const request = new Request(process.env.apiDomain)
 
export async function getServerSideProps({req, res, query}) {
    return {
        props: await initialProps(req, res, query)
    }
}

const getImageSource = (article) => {
    const thumbnail = article.theThumbnail || article.thumbnail || article.featured_image || article.image;
    
    if (!thumbnail || thumbnail === '' || thumbnail === 'null') {
        return params.noimage;
    }
    
    // Make relative URLs absolute for non-localhost environments
    if (thumbnail.startsWith('/') && !thumbnail.startsWith('http')) {
        const baseUrl = process.env.apiDomain || window.location.origin;
        return `${baseUrl.replace('/api', '')}${thumbnail}`;
    }
    
    return thumbnail;
};

const SectionWrapper = (props) => {
   
    const router = useRouter()
    const route = router.query.section
    const section = sectionData.filter((e) => e.route == route)

    if (!section[0])
        return <Error statusCode="404" />

    const cat1 = categories.slice(0,7);
    const cat2 = categories.slice(7,12);

    const userId = props.auth?.site_owner ? props.auth.userId : null
    const contactId = props.auth?.site_user ? props.auth.contactId : null;
    const accountId = props.site ? props.site.accountId : null;

    const {data: profile} = request.account.swrAccountProfile(accountId);
    const {data: shaklee } = request.account.swrShakleeInfo(accountId, profile ? profile.user.id : profile);
    
    const country = shaklee && shaklee.shaklee_country ? shaklee.shaklee_country : '232';

    const { data: info } = request.account.swrAccountProfileData(accountId, "account_user_trial_info");
    const config = info && info.data ? JSON.parse(info.data) : ''
    const tag = config ? config.company_name : 'Shaklee';

    // Fetch data for search functionality 
    const { data: recentArticles } = request.content.swrArticles({ section: "wellness-center", tags: [tag], except_types: [13], sort:"DATE_DESC", limit:50, user_id: userId, contact_id: contactId, country: country })
    const { data: replaysArticles } = request.content.swrArticles({ section: "wellness-center", tags: [tag], type:11, sort:"DATE_DESC", limit:20, user_id: userId, contact_id: contactId, country: country })
    const { data: videosArticles } = request.content.swrArticles({ section: "wellness-center", tags: [tag], type:2, sort:"DATE_DESC", limit:20, user_id: userId, contact_id: contactId, country: country })

    // Create lightweight search index using useMemo for performance
    const searchableItems = useMemo(() => {
      // Combine all article arrays
      const allArticles = [
        ...(recentArticles || []),
        ...(replaysArticles || []),
        ...(videosArticles || [])
      ];
      
      const uniqueArticlesMap = new Map();
      
      // Create lightweight objects with only needed properties
      allArticles.forEach(article => {
        if (!uniqueArticlesMap.has(article.id)) {
            uniqueArticlesMap.set(article.id, {
                id: article.id,
                slug: article.slug,
                title: article.title
            });
        }
      });
    
      // Convert map values to array
      return Array.from(uniqueArticlesMap.values());
    }, [recentArticles, replaysArticles, videosArticles]);

    const limit = 20;
    const [search, setSearch] = useState("")
    const [offset, setOffset] = useState(0)
    const [filter, setFilter] = useState(null)
    const [sort, setSort] = useState("DATE_DESC")
    const [articles, setArticles] = useState([])
    const [more, hasMore] = useState(true)
    const [isLoading, setIsLoading] = useState(false)
    const [fetchError, setFetchError] = useState(null)
    
    // Add ref to track fetch requests
    const fetchRequestRef = useRef(null)
    const isInitializedRef = useRef(false)

    // IMPROVED: Better reset function
    const reset = useCallback(function () {
        console.log("Resetting wellness component state");
        
        // Cancel any ongoing fetch request
        if (fetchRequestRef.current) {
            fetchRequestRef.current.cancelled = true;
        }
        
        hasMore(true)
        setArticles([])
        setOffset(0)
        setIsLoading(false)
        setFetchError(null)
    }, [])

    // IMPROVED: Enhanced fetch function with better error handling
    const fetch = useCallback(async function (slugOverride = null, tagOverride = null) {
        // Don't fetch if already loading
        if (isLoading) {
            console.log("Already loading, skipping fetch");
            return;
        }
        
        console.log(`Fetching wellness articles: filter=${filter}, sort=${sort}, offset=${offset}`);
        setIsLoading(true)
        setFetchError(null)
        
        // Create a request object that can be cancelled
        const currentRequest = { cancelled: false };
        fetchRequestRef.current = currentRequest;
        
        try {
            let data = []
            var category = slugOverride !== null ? slugOverride : (filter !== null ? filter : (typeof window !== 'undefined' ? localStorage.getItem('filter') : null));
            const sanitizedSearch = search ? encodeURIComponent(search) : "";
            
            const requestParams = {
                section: "wellness-center",
                type: section[0].id, 
                search: sanitizedSearch, 
                sort: sort, 
                limit: limit, 
                offset: offset, 
                user_id: userId, 
                contact_id: contactId,
                country: country 
            };
            
            if (process.env.apiDomain !== "http://api.dev.local/") {
                requestParams.tags = [tagOverride || tag];
            }
            
            if(category){
                requestParams.category = category
            }
            
            // Enhanced debugging
            console.log('=== ENHANCED API DEBUG ===');
            console.log('API Domain:', process.env.apiDomain);
            console.log('Request params:', requestParams);
            
            // Add timing
            const startTime = Date.now();
            data = await request.content.articles(requestParams);
            const endTime = Date.now();
            
            // Check if request was cancelled
            if (currentRequest.cancelled) {
                console.log("Request was cancelled, ignoring response");
                return;
            }
            
            console.log(`API Response time: ${endTime - startTime}ms`);
            console.log('Raw API Response:', data);
            console.log('Data length:', data ? data.length : 'null/undefined');
            
            if (!data || !data.length) {
                console.log('=== NO DATA RETURNED ===');
                hasMore(false)
                
                // Don't clear articles if we're paginating
                if (offset === 0) {
                    setArticles([]);
                }
                setIsLoading(false)
                return
            }
            
            // Update articles
            setArticles(prevArticles => {
                const newArticles = offset === 0 ? data : [...prevArticles, ...data];
                return newArticles;
            });
            
            let o = offset + limit
            setOffset(o)
            
            if (data.length < limit) {
                hasMore(false)
            }
            
        } catch (error) {
            // Check if request was cancelled
            if (currentRequest.cancelled) {
                console.log("Request was cancelled, ignoring error");
                return;
            }
            
            console.error('=== API ERROR ===');
            console.error('Error details:', error);
            setFetchError(error.message || "Error loading content");
            hasMore(false);
            
            // Don't clear articles if we're paginating
            if (offset === 0) {
                setArticles([]);
            }
        } finally {
            // Only set loading to false if request wasn't cancelled
            if (!currentRequest.cancelled) {
                setIsLoading(false)
            }
        }
    }, [filter, sort, offset, search, section, userId, contactId, country, tag, limit, isLoading])

    // FIXED: Better filter and sort change handling
    useEffect(() => {
        console.log(`Filter or sort changed: filter=${filter}, sort=${sort}`);
        reset()
        
        // Fetch after a small delay to ensure state has settled
        const timeoutId = setTimeout(() => {
            fetch(null, tag);
        }, 100);
        
        return () => clearTimeout(timeoutId);
    }, [filter, sort, reset, fetch, tag])

    // FIXED: Better initialization
    useEffect(() => {
        if (!isInitializedRef.current && typeof window !== 'undefined') {
            let filterValue = localStorage.getItem('filter');
            setFilter(filterValue);
            isInitializedRef.current = true;
        }
        
        if (offset === 0 && isInitializedRef.current && !isLoading && tag) {
            const timeoutId = setTimeout(() => {
                fetch(null, tag)
            }, 100);
            
            return () => clearTimeout(timeoutId);
        }
    }, [offset, tag, fetch, isLoading])

    const classes = "text-reset border-0 rounded-0 bg-transparent px-4 py-3"

    const [isSticky, setSticky] = useState(false);
    const ref = useRef(null);
    const handleScroll = () => {
      if (ref.current) {
        setSticky(ref.current.getBoundingClientRect().top <= 0);
      }
    };

    // IMPROVED: Better button handler
    const toggleButton = useCallback((slug) => {
        console.log(`Button toggled: ${slug}`);
        
        if (typeof window !== 'undefined') {
            if(slug){
                setFilter(slug);
                localStorage.setItem('filter', slug); 
            }
            else{
                localStorage.setItem('filter', ''); 
                setFilter(null);
            }
        }
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
  
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Add loading timeout
    useEffect(() => {
        let timeoutId = null;
        
        if (isLoading) {
            timeoutId = setTimeout(() => {
                console.log("Loading timeout reached - forcing completion");
                setIsLoading(false);
                
                if (articles.length === 0 && offset === 0) {
                    setFetchError("Loading timeout - please try again");
                }
            }, 15000);
        }
        
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [isLoading, articles.length, offset]);

    return(
        <WellnessLayout global={props} searchItems={searchableItems}>
            <div className="videolibrary wellnessCenter mb-5">
                <HeroWrapper 
                    title={section[0].long_name}
                    description={section[0].text}
                    classname={`bg-${section[0].route}`}
                />
                <div className={`sticky-wrapper text-center bg-white position-sticky top-0 zindex-1 ${isSticky ? ' sticky' : ''}`} ref={ref}>
                    <div className="d-md-block d-none">
						<Button active={!filter} onClick={() => toggleButton(null)} className={classes}>All</Button>
						{ cat1 ? cat1.map( (data,idx) => 
							<Button key={idx} active={filter === data.slug} onClick={() => toggleButton(data.slug)} className={classes}>
								{data.name}
							</Button>
						) : "" }
						{ cat2 ? 
							<DropdownButton id="dropdown-more-button" title="More" className="d-inline-block bg-white dp-more" variant="" alignRight>
							{ cat2.map( (data,idx) => {
								if (data.slug == "recipes")
									return false
								return (
									<Dropdown.Item key={idx} as="button" onClick={() => toggleButton(data.slug)} className="text-reset"><img width="30" src={data.icon} className="mr-1 moreitem"/>{data.name}</Dropdown.Item>
								)
							}) }
							</DropdownButton>   
						: "" }
					</div>
                    <div className="d-md-none d-block">						
						<Dropdown className="py-md-3 py-2">
							<Dropdown.Toggle variant="secondary" id="dropdown-basic"><FaBars/> Categories</Dropdown.Toggle>
							<Dropdown.Menu>
								<Dropdown.Item onClick={() => toggleButton(null)}>All</Dropdown.Item>
								{ cat1 ? cat1.map( (data,idx) => 
									<Dropdown.Item key={idx} onClick={() => toggleButton(data.slug)}>{data.name}</Dropdown.Item>
								) : "" }
								{ cat2 ? cat2.map( (data,idx) => 
									<Dropdown.Item key={idx} onClick={() => toggleButton(data.slug)}>{data.name}</Dropdown.Item>
								) : "" }
							</Dropdown.Menu>
						</Dropdown>
					</div>
                    <hr className="mt-0"/>
                </div>
                <div className="wellnessCenter cardscategory m-auto">
                    <div className="d-flex align-items-center">
                        <DropdownButton id="dropdown-basic-button" title={<><FaSortAmountDown/> Sort: { params.sort[sort] } <FaChevronDown className="h7 ml-3" size={9}/></>} className="sortdropdown my-5">
                            <Dropdown.Item onClick={() => setSort("TITLE_ASC")}>{ params.sort.TITLE_ASC }</Dropdown.Item>
                            <Dropdown.Item onClick={() => setSort("DATE_DESC")}>{ params.sort.DATE_DESC }</Dropdown.Item>
                        </DropdownButton>
                    </div>
                    
                    {/* Show error message if there was an error */}
                    {fetchError && (
                        <div className="alert alert-warning text-center">
                            {fetchError}
                            <button 
                                className="btn btn-link btn-sm ml-2" 
                                onClick={() => {
                                    setFetchError(null);
                                    reset();
                                    setTimeout(() => fetch(null, tag), 100);
                                }}
                            >
                                Retry
                            </button>
                        </div>
                    )}
                    
                    <InfiniteScroll className="px-3" style={{overflow: "hidden"}}
                        dataLength={articles.length} 
                        next={() => fetch(null, tag)}
                        loader={<h4 className="text-center">Loading...</h4>}
                        hasMore={more}
                        endMessage={ 
                            !articles.length && !isLoading ? 
                                <h4 className="text-center">No Content</h4> : 
                            articles.length > 0 ? 
                                <h4 className="text-center">No more content</h4> : 
                                null 
                        }
                        >
                        <Row>
                            { 
                                articles.map((data,idx) => {

                                const theObject = data.watched_data ? JSON.parse(data.watched_data) : '';
                                const percentage = theObject ? theObject.percentage : 0
             
                                return (<Col md={3} sm={6} className="mb-4" key={`${data.id}-${idx}`} >
                                    <Link href={"/a/" + data.slug} >
                                        <a className="h7 text-reset text-decoration-none">    
                                            <Image
                                            src={getImageSource(data)}
                                            alt={data.title}
                                            width={310}
                                            height={160}
                                            sizes="(max-width: 768px) 100vw, 310px"
                                            placeholder="blur"
                                            blurDataURL={params.noimage}
                                            loading={idx < 4 ? 'eager' : 'lazy'}
                                            style={{ objectFit: 'cover' }}
                                            />
                                            
                                            <div className="mt-1 mb-3 pbar">
                                                {data.is_read == 1 && (
                                                    <p className="mb-0 read h8 text-right position-relative">
                                                        <FaCheck className="cardCheck" /> You read this
                                                    </p>
                                                )}
                                                {data.is_downloaded == 1 && (
                                                    <p className="read h8 text-right position-relative">
                                                        <FaCheck className="cardCheck" /> Downloaded
                                                    </p>
                                                )}
                                                {data.is_watched == 1 && <ProgressBar now={percentage} />}
                                            </div>
                                            <p>{data.title}</p>   
                                        </a>
                                    </Link>
                                </Col>)
                                }) 
                            }
                        </Row>
                    </InfiniteScroll>
                </div>
            </div>
            <style jsx global>
                {`
                    button.active{
                        border-bottom:2px solid #43a649!important;
                    }
                    #dropdown-more-button{
                        padding: 0 1.5rem!important;
                        position:relative;
                    }
                    .dp-more .dropdown-menu{
                        grid-template-columns: repeat(2, 1fr);
                        top: 21px!important;
                    
                    } 
                    .sticky-wrapper button{
                        border-bottom:3px solid transparent!important;
                    }
                    .sticky-wrapper button.active{
                        border-color:#43a649!important;
                    } 
                    .sticky {
                        box-shadow: 0 5px 5px -5px #333;
                    }
                    .sticky button.active{
                        background-color: #43a649!important;
                        color: white!important;
                        padding: .25rem 1rem!important;
                        border-radius: .25rem!important;
                    }
                    .dp-more .dropdown-item:nth-of-type(4) .moreitem{
                        width:25px!important;
                    }
                    .dp-more .dropdown-item.active, .dp-more .dropdown-item:active{
                        background-color:#f8f9fa!important;
                    }
                    .cardscategory{
                        max-width:1334px;
                    }

                    @media (max-width: 991px) {
                        .infinite-scroll-component__outerdiv{
                            text-align: center;
                        }
                        .wellnessCenter .pbar .read{
                            text-align:center!important;
                        }
                    }
                    @media (max-width: 767px) {
                        .sticky-wrapper.position-sticky{
                        	top: 48px !important;
                        }
                    }
                `}
            </style>
        </WellnessLayout>
    )
}

export default SectionWrapper